import {
	BBox,
	MapOptions,
	MapRehabLayer,
	MapRiskLayer,
	SourceOptions,
} from '@Types';
import { MapPosition, ReactMapProps, SourceType } from '@innovyze/inno-map';
import {
	assetsBaseUrl,
	getTilesBaseUrl,
	riskBaseUrl,
	tilesBaseUrl,
} from '@Apis';
import { defaultInspectionSources, otherOptions } from './index';
import {
	propertyLookupRehabResults,
	propertyLookupRiskScoreGroup,
	propertyLookupRiskScoreVersion,
} from './mapDefinitions.utils';

// Geojson layers need query params based on MapPosition so to only return minimum required
export interface QueryOptions {
	radiusDefault: number; // m
	boundMargin: number; // %
	useBBox: boolean;
	singlePage: boolean;
	precisionLonLat: number;
	precisionRadius: number;
}

export const queryOptionsDefault: QueryOptions = {
	radiusDefault: 5000, // m
	boundMargin: 1.02, // %
	useBBox: true,
	singlePage: true,
	precisionLonLat: 6,
	precisionRadius: 0,
};

const queryBBox = (opts: QueryOptions, bbox: BBox | undefined): string =>
	opts.useBBox && bbox && bbox.length === 4
		? `&bbox=${bbox[0].toFixed(opts.precisionLonLat)}` +
		  `,${bbox[1].toFixed(opts.precisionLonLat)}` +
		  `,${bbox[2].toFixed(opts.precisionLonLat)}` +
		  `,${bbox[3].toFixed(opts.precisionLonLat)}`
		: '';

const queryLLR = (
	opts: QueryOptions,
	position: MapPosition | undefined,
	radius: number | undefined,
): string =>
	position && radius
		? `&lon=${position.center[0].toFixed(opts.precisionLonLat)}` +
		  `&lat=${position.center[1].toFixed(opts.precisionLonLat)}` +
		  `&rad=${radius.toFixed(opts.precisionRadius)}`
		: '';

const queryOthers = (opts: QueryOptions): string =>
	opts.singlePage ? '&singlePage=1' : '';

const query = (
	opts: QueryOptions,
	position: MapPosition | undefined,
	radius: number | undefined,
	bbox: BBox | undefined,
): string =>
	queryLLR(opts, position, radius) +
	queryBBox(opts, bbox) +
	queryOthers(opts);

const url = (
	source: SourceOptions,
	opts: QueryOptions,
	position: MapPosition | undefined,
	radius: number | undefined,
	bbox: BBox | undefined,
): string => `${source.url}?${query(opts, position, radius, bbox).slice(1)}`;

// Would like to use this rather than propertyLookupIndex
// but currently need to set propertyLookup to valid calls
// on first dataServices load - subsequent changes are
// ignored in inno-map
const propertyLookupVersion = (layers: MapRiskLayer[] = []) => {
	const p: Record<string, string> = {};
	for (let n = 0; n < layers.length; ++n) {
		p[`risk${n}`] = propertyLookupRiskScoreVersion(layers[n].versionId);
	}
	return p;
};

const propertyLookupIndex = (riskGroupCount = 0) => {
	const p: Record<string, string> = {};
	for (let n = 0; n < riskGroupCount; ++n) {
		p[`risk${n}`] = propertyLookupRiskScoreGroup(n);
	}
	return p;
};

const propertyLookupRehab = (layers: MapRehabLayer[] = []) => {
	const p: Record<string, string> = {};
	for (let n = 0; n < layers.length; ++n) {
		p[`rehab${n}`] = propertyLookupRehabResults(
			layers[n].id,
			layers[n].lastRun,
			layers[n].assetType,
		);
	}
	return p;
};

export const dataServicesMainMap = (
	options: MapOptions,
	token: string | undefined,
	position: MapPosition | undefined,
	radius: number | undefined,
	bbox: BBox | undefined,
	riskGroupCountLimit = -1,
	isTiledInspectionsEnabled = false,
	isTiledRiskEnabled = false,
	isTilesV3Enabled = false,
	isTilesetReadFromCloudOsEnabled = false,
): ReactMapProps['dataServices'] => {
	if (!token) {
		return [];
	}

	// Resolve the tiles host from the live flag rather than trusting the
	// baked-in `.url` / module-level `tilesBaseUrl` (both frozen at import,
	// before flags existed). Flag off returns the identical legacy host.
	const tilesUrl = getTilesBaseUrl(isTilesetReadFromCloudOsEnabled);

	if (isTilesV3Enabled) {
		// When V3 is enabled, all data (including Risk) is in the V3 tileset response
		// No need for separate data services
		// BUT we still need propertyLookup for risk and rehab layers
		const propertyLookup = propertyLookupVersion(options.riskLayers ?? []);
		const rehabPropertyLookup = propertyLookupRehab(
			options.rehabLayers ?? [],
		);

		return [
			{
				id: 'tiles',
				type: SourceType.INFO360_TILES_V3,
				url: tilesUrl,
				token: token,
				propertyLookup: {
					...propertyLookup,
					...rehabPropertyLookup,
				},
				attributesUrl: assetsBaseUrl,
			},
		];
	}

	// Would like to use version rather than index
	// but currently need to set propertyLookup to valid calls
	// on first dataServices load - as subsequent changes are
	// ignored in inno-map
	const propertyLookup =
		!isTiledRiskEnabled && riskGroupCountLimit > 1
			? propertyLookupIndex(riskGroupCountLimit)
			: propertyLookupVersion(options.riskLayers ?? []);

	const rehabPropertyLookup = propertyLookupRehab(options.rehabLayers ?? []);

	const tiles = options.tilesSource
		? [
				{
					id: options.tilesSource.dataServiceId,
					type: isTilesV3Enabled
						? SourceType.INFO360_TILES_V3
						: options.tilesSource.type,
					url: tilesUrl,
					token: token,
					propertyLookup: {
						...propertyLookup,
						...rehabPropertyLookup,
					},
					attributesUrl: assetsBaseUrl,
				},
		  ]
		: [];

	const inspections: ReactMapProps['dataServices'] = [];

	if (position && !isTiledInspectionsEnabled) {
		const inspectionSources =
			options.inspectionSources ?? defaultInspectionSources;
		const inspectionQueryOptions =
			options.generalOptions ?? queryOptionsDefault;
		inspectionSources.forEach(s => {
			if ((s?.minZoom ?? 0) <= position.zoom) {
				inspections?.push({
					type: s.type,
					id: s.dataServiceId,
					displayName: s.layerName,
					token: token,
					url: url(s, inspectionQueryOptions, position, radius, bbox),
				});
			}
		});
	}

	const risk: ReactMapProps['dataServices'] = [];

	// Create risk tile data services if tiled risk is enabled and we have valid risk layers
	if (isTiledRiskEnabled && options.riskLayers) {
		const riskSources = options.riskLayers;
		riskSources.forEach(s => {
			risk.push({
				id: `Risk/${s.configId}`,
				type: SourceType.TILES,
				url: `${tilesUrl}/v1/tile-json/Risk/${s.configId}`,
				token: token,
			});
		});
	}

	const otherSource = options.otherSource ?? otherOptions;

	const other =
		otherSource && !isTilesV3Enabled
			? [
					{
						id: otherSource.dataServiceId,
						type: otherSource.type,
						// Swap the legacy tiles base for the flag-resolved host
						// while preserving the tile-json path. No-op when off.
						url: otherSource.url.replace(tilesBaseUrl, tilesUrl),
						token: token,
						attributesUrl: riskBaseUrl,
					},
			  ]
			: [];

	const tiledInspections =
		isTiledInspectionsEnabled && !isTilesV3Enabled
			? [
					{
						id: 'tiled-inspections',
						type: SourceType.TILES,
						url: `${tilesUrl}/v1/tile-json/Inspections`,
						token: token,
					},
			  ]
			: [];

	return [tiles, inspections, other, tiledInspections, risk].flat(2);
};
